/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { Link } from 'react-router-dom';
import { BlogPost } from '../../types';
import { Tag, ClockIcon } from '../common';
import { colors, borderRadius, shadows, transitions, typography } from '../../styles/theme';

interface BlogCardProps {
  post: BlogPost;
  variant?: 'default' | 'featured' | 'horizontal';
}

const cardStyles = css`
  background: ${colors.background.primary};
  border-radius: ${borderRadius.xl};
  overflow: hidden;
  box-shadow: ${shadows.sm};
  transition: all ${transitions.normal};
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${shadows.xl};

    img {
      transform: scale(1.05);
    }
  }
`;

const featuredCardStyles = css`
  ${cardStyles};

  @media (min-width: 768px) {
    display: grid;
    grid-template-columns: 1.2fr 1fr;
    min-height: 320px;
  }
`;

const horizontalCardStyles = css`
  ${cardStyles};
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 1rem;

  @media (min-width: 640px) {
    grid-template-columns: 180px 1fr;
  }
`;

const imageContainerStyles = css`
  position: relative;
  overflow: hidden;
  aspect-ratio: 16/10;

  a {
    display: block;
    height: 100%;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    background: ${colors.neutral[100]};
    transition: transform ${transitions.slow};
  }
`;

const featuredImageStyles = css`
  ${imageContainerStyles};
  aspect-ratio: 16/9;

  @media (min-width: 768px) {
    aspect-ratio: auto;
    height: 100%;
  }
`;

const horizontalImageStyles = css`
  ${imageContainerStyles};
  aspect-ratio: 1;
  border-radius: ${borderRadius.lg};
  margin: 1rem 0 1rem 1rem;
`;

const contentStyles = css`
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const featuredContentStyles = css`
  ${contentStyles};
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (min-width: 768px) {
    padding: 2rem;
  }
`;

const horizontalContentStyles = css`
  padding: 1rem 1rem 1rem 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const categoryStyles = css`
  margin-bottom: 0.75rem;
`;

const titleStyles = css`
  font-family: ${typography.fontFamily.heading};
  font-size: ${typography.fontSize.xl};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.text.primary};
  margin-bottom: 0.75rem;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  a {
    color: inherit;
    text-decoration: none;

    &:hover {
      color: ${colors.primary[700]};
    }
  }
`;

const featuredTitleStyles = css`
  ${titleStyles};
  font-size: ${typography.fontSize['2xl']};

  @media (min-width: 768px) {
    font-size: ${typography.fontSize['3xl']};
  }
`;

const horizontalTitleStyles = css`
  ${titleStyles};
  font-size: ${typography.fontSize.base};
  -webkit-line-clamp: 2;

  @media (min-width: 640px) {
    font-size: ${typography.fontSize.lg};
  }
`;

const excerptStyles = css`
  color: ${colors.text.secondary};
  font-size: ${typography.fontSize.sm};
  line-height: 1.6;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin-bottom: 1rem;
`;

const metaStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: auto;
  font-size: ${typography.fontSize.sm};
  color: ${colors.text.secondary};
`;

const authorStyles = css`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  img {
    width: 28px;
    height: 28px;
    border-radius: ${borderRadius.full};
    object-fit: cover;
  }
`;

const readTimeStyles = css`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  white-space: nowrap;

  svg {
    width: 14px;
    height: 14px;
  }
`;

export default function BlogCard({ post, variant = 'default' }: BlogCardProps) {
  const isFeature = variant === 'featured';
  const isHorizontal = variant === 'horizontal';

  const cardCss = isFeature
    ? featuredCardStyles
    : isHorizontal
    ? horizontalCardStyles
    : cardStyles;

  const imageCss = isFeature
    ? featuredImageStyles
    : isHorizontal
    ? horizontalImageStyles
    : imageContainerStyles;

  const contentCss = isFeature
    ? featuredContentStyles
    : isHorizontal
    ? horizontalContentStyles
    : contentStyles;

  const titleCss = isFeature
    ? featuredTitleStyles
    : isHorizontal
    ? horizontalTitleStyles
    : titleStyles;

  return (
    <article css={cardCss}>
      <div css={imageCss}>
        <Link to={`/blog/${post.slug}`} tabIndex={-1} aria-hidden="true">
          <img src={post.coverImage} alt="" loading="lazy" />
        </Link>
      </div>

      <div css={contentCss}>
        <div css={categoryStyles}>
          <Tag label={post.category.name} color={post.category.color} />
        </div>

        <h3 css={titleCss}>
          <Link to={`/blog/${post.slug}`}>{post.title}</Link>
        </h3>

        {!isHorizontal && <p css={excerptStyles}>{post.excerpt}</p>}

        <div css={metaStyles}>
          <div css={authorStyles}>
            <img src={post.author.avatar} alt="" />
            <span>{post.author.name}</span>
          </div>
          <div css={readTimeStyles}>
            <ClockIcon />
            <span>{post.readTime} dk okuma</span>
          </div>
        </div>
      </div>
    </article>
  );
}
